/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 单张照片标签操作 hook (chip 删除 / chip 替换)
 *
 * 抽离自 Photo/index.tsx (review #4): 让 Photo / PhotoWithTag 只关心 UI,
 * 业务逻辑 (API 调用 + 乐观更新 + 埋点) 集中在 hook.
 *
 * 输入: 单张 photo + groupId + fromPage 埋点上下文
 * 输出: 操作 state (localLabels / chipBusyId / replaceTargetLabel)
 *      + handlers (handleChipRemove / handleChipReplaceCommit)
 *
 * 设计要点:
 * - localLabels 是受控本地 state, 切换 photo 时 useEffect 同步重置成 photo.labels
 * - 单照片操作除了更新 localLabels, 还 dispatch redux 兜底虚拟列表回收时数据丢失
 *   (虚拟列表会复用 DOM 节点, photo 引用变化时 useEffect 会把 localLabels 重置回 redux 的值)
 * - 替换流程用「unbind 老 + bind 新 (exclusive=true)」双保险: 后端 exclusive 没生效时也不会变 2 张
 */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { message } from 'antd'
import { useDispatch } from 'react-redux'
import { getIn } from '../../../utils/photoUtils'
import { invalidateTeamPhotoTagsCache } from '../../../app/hooks/useTeamPhotoTags'
import { useAppSelector } from '../../../app/hooks'
import type { PhotoCardTag } from '../../components/PhotoCardTagOverlay'
import { batchBindLabels, batchUnbindLabels } from '../api'
import {
  appendLabelsToPhotos,
  updateLabelsAcrossPhotos
} from '../../../slices/photosByContentSlice'
import {
  trackPicspageClick,
  trackPicspageDialogClick,
  trackPicspageToast,
  trackPicspageResult
} from '../utils/track'

export interface UsePhotoTagOperationsParams {
  photo: any
  groupId: string
  /** 埋点 fromPage. PhotoWithTag 从 BatchTagModeContext 透传 */
  fromPage: 'photov2' | 'ai_photo_king' | 'photo'
}

export interface UsePhotoTagOperationsReturn {
  /** 当前照片显示的标签 (受控本地 state) */
  localLabels: PhotoCardTag[]
  /** 外部 (如 PhotoCardTagOverlay 的 onLabelsChange) 直接覆盖 localLabels */
  setLocalLabels: Dispatch<SetStateAction<PhotoCardTag[]>>
  /** 哪个 chip 正在异步操作中 (loading) */
  chipBusyId: string | null
  /** 哪个 chip 的替换 popover 是 open 状态 */
  replaceTargetLabel: PhotoCardTag | null
  setReplaceTargetLabel: Dispatch<SetStateAction<PhotoCardTag | null>>
  /** chip × 点击 → 移除单个标签 */
  handleChipRemove: (lid: string) => Promise<void>
  /** chip 替换 popover 选了新标签 → 提交替换 */
  handleChipReplaceCommit: (newTag: PhotoCardTag) => Promise<void>
}

export function usePhotoTagOperations(
  params: UsePhotoTagOperationsParams
): UsePhotoTagOperationsReturn {
  const { photo, groupId, fromPage } = params

  const dispatch = useDispatch()
  const myUserId = useAppSelector(
    (state: any) => state.main?.user?.user_id
  ) as string

  const photoFileForOp = getIn(photo, ['file']) as string
  const photoSeqForOp = getIn(photo, ['seq']) as number

  // 初始 labels = photo.labels (后端下发), 切 photo 时 useEffect 重置
  const initialLabels: PhotoCardTag[] = useMemo(() => {
    const labels = getIn(photo, ['labels'])
    if (!Array.isArray(labels)) return []
    return labels.map((l: any) => ({
      id: String(l.id),
      name: l.name,
      color: l.color
    }))
  }, [photo])
  const [localLabels, setLocalLabels] = useState<PhotoCardTag[]>(initialLabels)
  useEffect(() => {
    setLocalLabels(initialLabels)
  }, [initialLabels])

  const [replaceTargetLabel, setReplaceTargetLabel] =
    useState<PhotoCardTag | null>(null)
  const [chipBusyId, setChipBusyId] = useState<string | null>(null)

  const handleChipRemove = useCallback(
    async (lid: string) => {
      if (!photoFileForOp || !groupId) return
      const removed = localLabels.find((l) => String(l.id) === String(lid))
      trackPicspageClick({
        clickItem: 'card_chip_remove',
        fromPage,
        string1: lid,
        labelName: removed?.name,
        tabName: 'team_label'
      })
      setChipBusyId(lid)
      const t0 = Date.now()
      try {
        await batchUnbindLabels({
          groupID: String(groupId),
          filter: { include: [photoFileForOp], exclude: [] },
          labelIDs: [lid]
        })
        setLocalLabels((prev) =>
          prev.filter((l) => String(l.id) !== String(lid))
        )
        if (typeof photoSeqForOp === 'number') {
          dispatch(
            updateLabelsAcrossPhotos({
              deletedIds: [lid],
              affectedSeqs: [photoSeqForOp]
            })
          )
        }
        trackPicspageResult({
          actionName: 'unbind_label_single',
          actionResult: 'success',
          costTime: Date.now() - t0,
          photoNum: 1,
          number1: 1,
          list1: removed ? [removed.name] : [],
          tabName: 'team_label',
          fromPage
        })
        trackPicspageToast({
          toastName: 'single_unbind_success',
          fromPage
        })
      } catch (err) {
        const msg = (err as Error)?.message || '移除失败,请稍后再试'
        message.error(msg)
        trackPicspageResult({
          actionName: 'unbind_label_single',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: 1,
          number1: 1,
          list1: removed ? [removed.name] : [],
          tabName: 'team_label',
          fromPage
        })
        trackPicspageToast({
          toastName: 'single_unbind_fail',
          fromPage
        })
      } finally {
        setChipBusyId(null)
      }
    },
    [groupId, photoFileForOp, photoSeqForOp, dispatch, localLabels, fromPage]
  )

  const handleChipReplaceCommit = useCallback(
    async (newTag: PhotoCardTag) => {
      const old = replaceTargetLabel
      if (!old || !photoFileForOp || !groupId) {
        setReplaceTargetLabel(null)
        return
      }
      // 埋点: dialog_click select_tag (替换 popover 内选了新标签)
      trackPicspageDialogClick({
        popTitle: 'card_chip_replace_popover',
        clickItem: 'select_tag',
        fromPage,
        string1: String(newTag.id),
        labelName: newTag.name,
        tabName: 'team_label'
      })
      if (String(newTag.id) === String(old.id)) {
        setReplaceTargetLabel(null)
        return
      }
      setChipBusyId(String(old.id))
      setReplaceTargetLabel(null)
      const t0 = Date.now()
      try {
        // 双保险: 后端 exclusive=true 还没部署时 (老版本忽略 exclusive 走 append).
        // 显式调一次 unbind 把当前所有 labels 清掉, 再 bind 新的 → 不依赖后端版本.
        // 后端 exclusive 部署后这步是 no-op (后端 exclusive 自己清), 不会有副作用.
        const oldIds = localLabels.map((l) => String(l.id))
        if (oldIds.length > 0) {
          await batchUnbindLabels({
            groupID: String(groupId),
            filter: { include: [photoFileForOp], exclude: [] },
            labelIDs: oldIds
          })
        }
        await batchBindLabels({
          groupID: String(groupId),
          filter: { include: [photoFileForOp], exclude: [] },
          labelIDs: [String(newTag.id)],
          exclusive: true
        })
        const next: PhotoCardTag = {
          id: String(newTag.id),
          name: newTag.name,
          color: newTag.color || '#2979FF'
        }
        setLocalLabels([next])
        if (typeof photoSeqForOp === 'number') {
          dispatch(
            appendLabelsToPhotos({
              affectedSeqs: [photoSeqForOp],
              newLabels: [next],
              exclusive: true
            })
          )
        }
        // 用 count 变化后失效团队标签缓存, 让 useCount 拿到最新
        invalidateTeamPhotoTagsCache(String(groupId || ''), myUserId)
        trackPicspageResult({
          actionName: 'bind_label_single_exclusive',
          actionResult: 'success',
          costTime: Date.now() - t0,
          photoNum: 1,
          number1: 1,
          list1: [newTag.name],
          tabName: 'team_label',
          bool1: true,
          fromPage
        })
        trackPicspageToast({
          toastName: 'single_replace_success',
          fromPage
        })
      } catch (err) {
        const msg = (err as Error)?.message || '替换失败,请稍后再试'
        message.error(msg)
        trackPicspageResult({
          actionName: 'bind_label_single_exclusive',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: 1,
          number1: 1,
          list1: [newTag.name],
          tabName: 'team_label',
          bool1: true,
          fromPage
        })
        trackPicspageToast({
          toastName: 'single_replace_fail',
          fromPage
        })
      } finally {
        setChipBusyId(null)
      }
    },
    [
      groupId,
      photoFileForOp,
      photoSeqForOp,
      replaceTargetLabel,
      localLabels,
      dispatch,
      myUserId,
      fromPage
    ]
  )

  return {
    localLabels,
    setLocalLabels,
    chipBusyId,
    replaceTargetLabel,
    setReplaceTargetLabel,
    handleChipRemove,
    handleChipReplaceCommit
  }
}
